import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import { Icon } from "@iconify/react/dist/iconify.js";
import { addUser, updateUser } from "../../services/userService";

const toastCfg = { autoClose: 1500 };

export const initForm = {
  name: "", mobile: "",
  password: "", my_refer: "",
  role: "user", status: 1, remark: "",
};

const Field = ({ label, required, error, children, style }) => (
  <div style={style}>
    <label style={ms.label}>
      {label}{required && <span style={{ color: "#ef4444" }}> *</span>}
    </label>
    {children}
    {error && <div style={ms.fieldErr}>{error}</div>}
  </div>
);

export const UserModal = ({ show, onHide, editUser, onSaved }) => {
  const isEdit = Boolean(editUser?.id);
  const [form, setForm] = useState(initForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setErrors({});
      setForm(
        isEdit
          ? {
              name:     editUser.name      || "",
              mobile:   editUser.mobile    || "",
              password: "",
              my_refer: editUser.my_refer  || "",
              role:     editUser.role      || "user",
              status:   editUser.status    ?? 1,
              remark:   editUser.remark    || "",
            }
          : initForm
      );
    }
  }, [show, editUser, isEdit]);

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setErrors((er) => ({ ...er, [field]: "" }));
  };

  const validate = () => {
    const err = {};
    if (!form.name.trim())                    err.name     = "Name is required";
    if (!/^[0-9]{10}$/.test(form.mobile))     err.mobile   = "Enter valid 10-digit mobile";
    if (!isEdit && form.password.length !== 6) err.password = "Password must be exactly 6 characters";
    if (Number(form.status) === 0 && !form.remark.trim())
      err.remark = "Remark required for inactive user";
    setErrors(err);
    return !Object.keys(err).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const res = isEdit
        ? await updateUser(editUser.id, form)
        : await addUser(form);

      if (res.status === false) {
        toast.error(res.msg || "Operation failed", toastCfg);
      } else {
        toast.success(isEdit ? "User updated" : "User added", toastCfg);
        onSaved();
        onHide();
      }
    } catch {
      toast.error("Server error", toastCfg);
    }
    setSaving(false);
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
      <div style={ms.header}>
        <div>
          <div style={ms.headerTitle}>{isEdit ? "Edit User" : "Add New User"}</div>
          <div style={ms.headerSub}>
            {isEdit ? `Editing: ${editUser?.name}` : "Fill in the details to create a new user"}
          </div>
        </div>
        <button style={ms.closeBtn} onClick={onHide}>
          <Icon icon="radix-icons:cross-2" style={{ fontSize: 18 }} />
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={ms.body}>
          <div style={ms.grid}>
            {/* Name */}
            <Field label="Full Name" required error={errors.name}>
              <input style={ms.input} placeholder="Enter full name"
                value={form.name} onChange={set("name")} />
            </Field>
            {/* Mobile */}
            <Field label="Mobile Number" required error={errors.mobile}>
              <input style={ms.input} placeholder="10-digit mobile"
                value={form.mobile} onChange={set("mobile")} />
            </Field>
            {/* Password — add only */}
            {!isEdit && (
              <Field label="Password" required error={errors.password}>
                <input style={ms.input} type="password" placeholder="Exactly 6 characters"
                  value={form.password} onChange={set("password")} />
              </Field>
            )}
            {/* Referral */}
            <Field label="Referral Code">
              <input style={ms.input} placeholder="Optional"
                value={form.my_refer} onChange={set("my_refer")} />
            </Field>
            {/* Role */}
            <Field label="Role" required>
              <select style={ms.input} value={form.role} onChange={set("role")}>
                <option value="user">User</option>
                <option value="agent">Agent</option>
              </select>
            </Field>
            {/* Status */}
            <Field label="Status" required>
              <select style={ms.input} value={form.status} onChange={set("status")}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </Field>
          </div>

          {/* Remark — only when inactive */}
          {Number(form.status) === 0 && (
            <Field label="Remark (Inactive reason)" required error={errors.remark}
              style={{ marginTop: 12 }}>
              <textarea style={{ ...ms.input, minHeight: 72, resize: "vertical" }}
                placeholder="Enter reason…" rows={3}
                value={form.remark} onChange={set("remark")} />
            </Field>
          )}
        </div>

        <div style={ms.footer}>
          <button type="button" style={ms.cancelBtn} onClick={onHide}>Cancel</button>
          <button type="submit" style={ms.saveBtn} disabled={saving}>
            {saving
              ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
              : isEdit ? "Update User" : "Add User"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const ms = {
  header: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    padding: "20px 24px 16px",
    borderBottom: "1px solid #f1f5f9",
  },
  headerTitle: { fontWeight: 800, fontSize: "1.05rem", color: "#0f172a" },
  headerSub: { fontSize: "0.78rem", color: "#94a3b8", marginTop: 2 },
  closeBtn: {
    background: "#f1f5f9", border: "none", borderRadius: 8,
    width: 32, height: 32, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#64748b",
  },
  body: { padding: "18px 24px" },
  grid: {
    display: "grid", gridTemplateColumns: "1fr 1fr",
    gap: "14px 18px",
  },
  label: {
    display: "block", fontSize: "0.75rem", fontWeight: 700,
    color: "#475569", marginBottom: 5,
    textTransform: "uppercase", letterSpacing: "0.04em",
  },
  input: {
    width: "100%", padding: "9px 12px",
    border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.87rem", color: "#1e293b",
    background: "#f8fafc", outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  fieldErr: { fontSize: "0.72rem", color: "#ef4444", marginTop: 4 },
  footer: {
    display: "flex", justifyContent: "flex-end", gap: 10,
    padding: "14px 24px 20px",
    borderTop: "1px solid #f1f5f9",
  },
  cancelBtn: {
    padding: "8px 18px", background: "#f1f5f9", border: "none",
    borderRadius: 8, fontFamily: "inherit", fontSize: "0.85rem",
    fontWeight: 600, color: "#475569", cursor: "pointer",
  },
  saveBtn: {
    padding: "8px 24px",
    background: "linear-gradient(135deg,#16632b,#1da144)",
    border: "none", borderRadius: 8, fontFamily: "inherit",
    fontSize: "0.85rem", fontWeight: 700, color: "#fff",
    cursor: "pointer", display: "flex", alignItems: "center",
  },
};
